import React from 'react';
import { Box, Paper, Stack, Typography, useTheme } from '@mui/material';
import { chartSeries } from '../../theme';

/** Axis/grid/legend props shared by every chart so they read as one system. */
export const useChartTheme = () => {
  const theme = useTheme();
  return {
    grid: { stroke: theme.palette.divider, strokeDasharray: '4 4', vertical: false },
    axis: {
      stroke: 'transparent',
      tick: { fill: theme.palette.text.secondary, fontSize: 11 },
      tickLine: false,
      axisLine: false,
    },
    legend: {
      iconType: 'circle' as const,
      wrapperStyle: { fontSize: 12, paddingTop: 12, color: theme.palette.text.secondary },
    },
    cursor: { fill: theme.palette.action.hover, stroke: theme.palette.divider },
    series: chartSeries,
  };
};

interface ChartTooltipProps {
  active?: boolean;
  payload?: any[];
  label?: string | number;
  /** Formats each value; defaults to a locale-aware integer. */
  format?: (value: number, name: string) => string;
}

/** Tooltip rendered as a themed surface instead of Recharts' white default. */
export const ChartTooltip: React.FC<ChartTooltipProps> = ({ active, payload, label, format }) => {
  if (!active || !payload || payload.length === 0) return null;

  return (
    <Paper
      elevation={0}
      sx={{
        px: 1.5,
        py: 1.25,
        minWidth: 168,
        borderRadius: 2,
        border: (theme) => '1px solid ' + theme.palette.divider,
        bgcolor: 'background.elevated',
        boxShadow: '0 12px 28px -8px rgba(15,23,42,0.18)',
      }}
    >
      {label !== undefined && (
        <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.75 }}>
          {label}
        </Typography>
      )}
      <Stack spacing={0.5}>
        {payload.map((entry, index) => (
          <Stack
            key={`${entry.dataKey}-${index}`}
            direction="row"
            alignItems="center"
            spacing={1}
            justifyContent="space-between"
          >
            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0 }}>
              <Box
                aria-hidden="true"
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  flexShrink: 0,
                  bgcolor: entry.color ?? entry.payload?.fill,
                }}
              />
              <Typography variant="caption" noWrap sx={{ color: 'text.secondary' }}>
                {entry.name}
              </Typography>
            </Stack>
            <Typography variant="caption" className="tnum" sx={{ fontWeight: 700 }}>
              {format
                ? format(Number(entry.value), String(entry.name))
                : Number(entry.value).toLocaleString()}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Paper>
  );
};
